import { CDN_URL } from "@/utils/config";
import axios from "axios";
import { ref } from "vue";

const areaDataUrl = `${CDN_URL}map/areas.json`;
/**
 * 地区库
 * 数据源 https://github.com/zengzhan/qqzeng-ip/blob/master/mysql/area.sql
 * 淘宝店铺  qqzeng-ip
 */
class AreaLib {
    public areaData = ref([]);
    private dataLoadPromise: Promise<void>;

    constructor() {
        this.dataLoadPromise = this.loadAreaData().then(() => {});
    }

    /** 加载地区数据 */
    private async loadAreaData() {
        try {
            const response = await axios.get(areaDataUrl);
            this.areaData.value = await response.data;
        } catch (error) {
            console.error("Failed to load area data:", error);
        }
    }

    /** 获取子地区列表（同步） */
    getAreaList = (provinceAdcode?: string, cityAdcode?: string) => {
        if (!this.areaData.value?.length) {
            return []; // 避免报错，返回空数组
        }

        let area = this.areaData.value;
        if (provinceAdcode) {
            area = (this.areaData.value.find(item => item.id === provinceAdcode)?.childs || []) as typeof area;
            if (cityAdcode) {
                area = (area.find(item => item.id === cityAdcode)?.childs || []) as typeof area;
            }
        }
        return area;
    };

    /** 根据adcode获取地区名 */
    getAreaName = (adcode?: string, options: { provinceAdcode?: string; cityAdcode?: string } = {}) => {
        const { provinceAdcode, cityAdcode } = options;
        const area = this.getAreaList(provinceAdcode, cityAdcode);
        return area.find(item => item.id === adcode)?.name;
    };

    /** 获取完整省市区名 */
    getFullAreaName = (provinceAdcode?: string, cityAdcode?: string, regionAdcode?: string) => {
        let fullName = "";
        if (provinceAdcode) {
            fullName += this.getAreaName(provinceAdcode) ?? "";
            if (cityAdcode) {
                fullName += this.getAreaName(cityAdcode, { provinceAdcode }) ?? "";
                if (regionAdcode) {
                    fullName += this.getAreaName(regionAdcode, { provinceAdcode, cityAdcode }) ?? "";
                }
            }
        }
        return fullName;
    };

    /** 根据adcode获取简称 */
    getAreaShortName = (adcode?: string, options: { provinceAdcode?: string; cityAdcode?: string } = {}) => {
        const { provinceAdcode, cityAdcode } = options;
        const area = this.getAreaList(provinceAdcode, cityAdcode);
        return area.find(item => item.id === adcode)?.shortName;
    };

    /** 获取完整省市简称 */
    getShortAreaName = (provinceAdcode: string, cityAdcode: string, regionAdcode?: string) => {
        let fullName =
            (this.getAreaShortName(provinceAdcode) ?? "") +
            (this.getAreaShortName(cityAdcode, { provinceAdcode }) ?? "") +
            (this.getAreaShortName(regionAdcode, { provinceAdcode, cityAdcode }) ?? "");
        // 定义直辖市名称列表
        const cities = ["北京", "上海", "天津", "重庆"];
        // 遍历每个直辖市名称并替换重复的部分
        cities.forEach(city => {
            const regex = new RegExp(`(${city})\\1`, "g"); // 正则表达式匹配重复的城市名称
            fullName = fullName.replace(regex, "$1"); // 将重复的城市名称替换为单个
        });
        return fullName;
    };

    /** areaLib列表转options列表 */
    areaList2Options = (areaList: typeof this.areaData) => {
        type Option = {
            label: string;
            value: any;
            children?: Option[];
        };
        return areaList.map(item => {
            const option: Option = {
                label: item.name,
                value: item.id
            };
            if (item.childs) {
                option.children = this.areaList2Options(item.childs as typeof this.areaData);
            }
            return option;
        });
    };

    /** 获取只获取两层地区列表 */
    getAreaListEndCity = () => {
        if (!this.areaData.value?.length) {
            return []; // 避免报错，返回空数组
        }
        return this.areaData.value.map(item => {
            if (item.childs?.length) {
                item.childs.forEach(child => {
                    delete child.childs;
                });
            }
            return item;
        });
    };
}

export const areaLib = new AreaLib();
